import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import * as d3 from 'd3'
import { ref, toRefs } from 'vue'
import { useComponent } from './useComponent'
import { ColorScaleConfig, getColorForValue } from '@/utils/colorScale'
import { Pattern } from '../chart/bars'

export type DataHighlightCircleProps = {
  positions: [number, number | null][]
  values?: [number | string, number | null][]
  radius: number
  colors: string[]
  offsets?: [number, number][]
  colorScale?: ColorScaleConfig
  visible?: boolean
  labels: string[]
  patterns?: Pattern[]
  strokeWidth?: number
  xLimits: [number, number]
  yLimits: [number, number]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DataHighlightCircleComponent',
  props: {
    positions: {},
    values: {},
    radius: {},
    colors: {},
    offsets: {},
    colorScale: {},
    visible: { type: Boolean },
    labels: {},
    patterns: {},
    strokeWidth: {},
    xLimits: {},
    yLimits: {}
  },
  setup(__props: any) {

const props = __props

const {
  positions,
  offsets,
  values,
  labels,
  xLimits,
  yLimits,
  strokeWidth,
  colors,
  radius,
  colorScale,
  patterns,
  visible
} = toRefs(props)

const group = ref<SVGGElement | null>(null)

let dotsSelection: d3.Selection<
  SVGCircleElement,
  [number, number | null],
  SVGGElement,
  unknown
> | null = null

useComponent(group, ({ width, height, group: g, parentGroup }) => {
  const xScale = d3.scaleLinear().domain(xLimits?.value).range([0, width])

  const yScale = d3.scaleLinear().domain(yLimits?.value).range([height, 0])
  const effectiveOffsets = offsets?.value ?? positions.value.map(() => [0, 0])

  dotsSelection = g
    .selectAll<SVGCircleElement, [number, number | null]>('circle.data-highlight-dot')
    .data(positions.value, (d, i) => `${d[0]}-${d[1]}-${i}`)
    .join(
      (enter) =>
        enter
          .append('circle')
          .classed('data-highlight-dot', true)
          .attr('r', radius.value)
          .attr('fill', 'white')
          .attr('opacity', '0')
          .attr('stroke-width', strokeWidth?.value ?? 2),
      (update) => update,
      (exit) => exit.remove()
    )

    .attr('data-x', (d) => d[0])
    .attr('data-y', (d) => d[1])
    .attr('data-value-x', (d, i) => values?.value?.[i]?.[0] ?? d[0])
    .attr('data-value-y', (d, i) => values?.value?.[i]?.[1] ?? d[1])
    .attr('data-tooltip-x', (d, i) => d[0] + (effectiveOffsets[i]?.[0] ?? 0))
    .attr('data-tooltip-y', (d, i) =>
      d[1] === null ? null : d[1] + (effectiveOffsets[i]?.[1] ?? 0)
    )
    .attr('data-label', (d, i) => labels.value?.[i] ?? '')
    .attr('data-pattern', (d, i) => JSON.stringify(patterns?.value?.[i] ? patterns.value[i] : null))
    .attr('data-color', (d, i) => {
      const yVal = d[1]

      return (
        (yVal !== null && colorScale?.value
          ? getColorForValue(colorScale.value, yVal).toHex()
          : colors.value?.[i]) ?? 'none'
      )
    })
    .attr('cx', (d, i) => xScale(d[0] + (effectiveOffsets[i]?.[0] ?? 0)))
    .attr('cy', (d, i) =>
      d[1] === null ? -radius.value * 2 : yScale(d[1] + (effectiveOffsets[i]?.[1] ?? 0))
    )
    .attr('display', (d) => (d[1] === null ? 'none' : 'block'))
    .attr('stroke', (d, i) => {
      if (visible?.value === false) return 'transparent'
      const yVal = d[1]

      return (
        (yVal !== null && colorScale?.value
          ? getColorForValue(colorScale.value, yVal).toHex()
          : colors.value?.[i]) ?? 'none'
      )
    })

  let viewportRect: d3.Selection<SVGRectElement, unknown, null, undefined> =
    parentGroup.select('.viewport-rect')
  if (viewportRect.empty()) {
    viewportRect = parentGroup
      .insert('rect', ':first-child')
      .classed('viewport-rect', true)
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('fill', 'transparent')
      .style('pointer-events', 'all')
  }

  const listenerId = '.highlight-circle' + Math.random().toString()
  viewportRect
    .on('mousemove' + listenerId, onMouseMove)
    .on('mouseenter' + listenerId, () => {
      /* Can potentially show all on enter if desired */
    })
    .on('mouseleave' + listenerId, () => {
      if (dotsSelection) {
        dotsSelection.attr('opacity', '0').classed('highlight', false)
      }
    })

  function onMouseMove(event: MouseEvent) {
    if (!dotsSelection || positions.value.length === 0) return

    const [mouseX, mouseY] = d3.pointer(event, parentGroup.node())

    let minDistSq = Infinity
    let closestIndex: number | null = null

    positions.value.forEach((pos, i) => {
      if (pos[1] === null) {
        return
      }

      const currentOffset = effectiveOffsets[i] ?? [0, 0]
      const screenX = xScale(pos[0] + currentOffset[0])
      const screenY = yScale(pos[1] + currentOffset[1])

      const dx = screenX - mouseX
      const dy = screenY - mouseY
      const distSq = dx * dx + dy * dy

      if (distSq < minDistSq) {
        minDistSq = distSq
        closestIndex = i
      }
    })

    if (closestIndex === null) {
      dotsSelection.attr('opacity', '0').classed('highlight', false)
      return
    }

    const closestPosition = positions.value[closestIndex]
    const targetX = closestPosition[0]
    const targetY = closestPosition[1]

    dotsSelection
      .attr('opacity', (d, i) => {
        const currentPos = positions.value[i]

        const isMatch = currentPos[0] === targetX && currentPos[1] === targetY
        return isMatch && currentPos[1] !== null ? '1' : '0'
      })
      .classed('highlight', (d, i) => {
        const currentPos = positions.value[i]

        return currentPos[0] === targetX && currentPos[1] === targetY && currentPos[1] !== null
      })
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("g", {
    ref_key: "group",
    ref: group
  }, null, 512))
}
}

})